import React from 'react';
import { Flex } from '@chakra-ui/react';
import { Button } from '@morressier/alembic';
import _ from 'lodash';
import { FormProvider, useForm } from 'react-hook-form';
import { IAuthorWithContribution } from '@publishing-engine/domain/models';
import { generateValidationSchema } from 'src/utils/validation';
import { ajvResolver } from '@hookform/resolvers/ajv';
import {
  FormFieldAuthors,
  FormFieldDefinition
} from '@publishing-engine/domain/types/article';
import { Overlay } from 'src/components/Overlay';
import { AuthorsForm, DUPLICATE_EMAIL_ERROR_TYPE } from './AuthorsForm';
import { ISubmissionAuthor } from 'src/contexts/FormContext';
import { IAuthorRolesMap } from 'src/utils/groupAuthorByRoles';

export type IAuthorsValue = {
  metadata: {
    authors: ISubmissionAuthor[];
  };

  showDiscardChangesModal: boolean;
};

interface Props {
  onSubmit: (value: IAuthorWithContribution[]) => void;
  onClose: () => void;
  formField: FormFieldAuthors;
  value?: IAuthorWithContribution[];
  authorRoleMap: IAuthorRolesMap;
  isSubmitting: boolean;
  isRevision?: boolean;
}

export const AuthorsUpdateModal: React.FC<Props> = props => {
  const {
    onSubmit,
    onClose,
    formField,
    value,
    authorRoleMap,
    isSubmitting,
    isRevision
  } = props;

  const defaultValues = _.set(
    {} as IAuthorsValue,
    formField.caseDataFields.main,
    value
  );

  const schema = generateValidationSchema({
    [formField.name]: formField
  } as Record<string, FormFieldDefinition>);

  const methods = useForm({
    defaultValues,
    resolver: ajvResolver(schema)
  });

  const handleSubmit = async () => {
    const data = methods.getValues();
    const authors =
      (_.get(data, formField.caseDataFields.main) as ISubmissionAuthor[]) || [];
    const emails = authors
      .map(a => a.email?.toLowerCase().trim())
      .filter(Boolean);
    const hasDuplicates = new Set(emails).size !== emails.length;

    if (hasDuplicates) {
      methods.setError(formField.caseDataFields.main, {
        type: DUPLICATE_EMAIL_ERROR_TYPE,
        message:
          'Duplicate email found. Please make sure each author has a unique email address.'
      });
      return;
    }

    const isValid = await methods.trigger(formField.caseDataFields.main);
    if (!isValid) {
      return;
    }

    onSubmit(_.get(data, formField.caseDataFields.main));
  };

  const handleClose = () => {
    if (methods.formState.isDirty) {
      methods.setValue('showDiscardChangesModal', true);
    } else {
      onClose();
    }
  };

  const hasValidationErrors = !!methods.formState.errors.metadata?.authors;

  return (
    <FormProvider {...methods}>
      <Overlay data-cy="author-list-edit-modal">
        <form onSubmit={methods.handleSubmit(handleSubmit)}>
          <Flex
            flexDirection="column"
            gap={8}
            height="100vh"
            overflow={'hidden'}
          >
            <AuthorsForm
              onClose={onClose}
              value={value}
              config={formField}
              authorRoleMap={authorRoleMap}
              isRevision={isRevision}
            />
            <Flex
              gap={2}
              width={'100%'}
              backgroundColor="surfacePrimaryFill"
              position={'sticky'}
              bottom={0}
              paddingX={5}
              paddingY={4}
              justifyContent={'space-between'}
            >
              <Button
                disabled={isSubmitting}
                onClick={handleClose}
                variant="caution"
              >
                Exit
              </Button>
              <Flex gap={2}>
                <Button
                  disabled={isSubmitting}
                  onClick={handleClose}
                  variant="secondary"
                >
                  Close
                </Button>
                <Button
                  type="submit"
                  isLoading={isSubmitting}
                  disabled={hasValidationErrors || isSubmitting}
                >
                  Save Changes
                </Button>
              </Flex>
            </Flex>
          </Flex>
        </form>
      </Overlay>
    </FormProvider>
  );
};
